import {
    Component,
    OnChanges,
    Input,
    OnInit,
    SimpleChanges,
    ViewChild,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    Output,
    EventEmitter,
    AfterViewChecked,
    ChangeDetectorRef,
} from '@angular/core';
import { ChannelHistory } from '@models/channel-history';
import { Channel, ChannelFns } from 'ops/common/models/channel';
import { Observable, Subscription, lastValueFrom } from 'rxjs';
import Dygraph from 'dygraphs';
import { ChannelService } from '@service/channel.service';
import domtoimage from 'dom-to-image';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { DygraphInteractionModel } from '@models/dygrpah-interaction-model';
import { ResponsiveService } from '@service/responsive.service';
import dayjs from 'dayjs';

@Component({
    selector: 'app-channel-graph',
    templateUrl: './channel-graph.component.html',
    styleUrls: ['./channel-graph.component.scss'],
    standalone: false,
})
export class ChannelGraphComponent
    implements OnInit, OnChanges, AfterViewInit, OnDestroy, AfterViewChecked
{
    @Output() onViewLoaded = new EventEmitter<void>();
    @Output() displayMinXIndexChange = new EventEmitter<number>();
    @Output() displayMaxXIndexChange = new EventEmitter<number>();

    @Input() onDataUpdate: Observable<ChannelHistory>;
    @Input() view: TrendGraphView;
    @Input() width: number = 1371;
    @Input() height: number = 587;
    @Input() maximumValues: number = 2000;
    @Input() maximumLines = 16;
    @Input() displayMinY: number = 0;
    @Input() displayMaxY: number = 100;
    @Input() displayMinXIndex: number = 0;
    @Input() displayMaxXIndex: number = this.maximumValues - 1;
    @Input() MIN_DATE_INDEX: number;
    @Input() MAX_DATE_INDEX: number;
    @Input() graphMinHeight: string;

    @ViewChild('tooltip') tooltip: ElementRef;
    @ViewChild('parentElement') parentElement: ElementRef;
    @ViewChild('graphContainer') graphContainer: ElementRef;
    @ViewChild('pointer') pointer: ElementRef;

    POINTER_WIDTH = 18;
    DIGITAL_CHANNEL_START = 6;
    DIGITAL_CHANNEL_DISTANCE = 15;
    axisLabelFontSize: number = 14;

    digitalSeriesOptions = null;

    graph: any = null;

    graphValues: number[][] = [];
    labels: string[];
    colors: string[];
    dateRange: [number, number] = [undefined, undefined];
    valueRange: [number, number] = [undefined, undefined];

    visualUpdatesPaused: boolean = false;

    labelToColorMap: { [label: string]: string } = {};
    labelToChannelMap: { [label: string]: Channel } = {};
    labelToScaleMap: { [label: string]: number } = {};
    digitalChannelCount = 0;

    tooltipStyle = {
        display: 'none',
        left: '0px',
        right: null,
        top: '0px',
    };
    tooltipDate: Date = new Date();
    tooltipData: { label: string; value: any; unit: string }[] = [];

    verticalRangefinderStyle = {
        display: 'none',
        left: '0px',
    };
    horizontalRangefinderStyle = {
        display: 'none',
        top: '0px',
    };
    pointerStyle = {
        display: 'none',
        left: '0px',
        top: '0px',
    };

    //Used for offsetting digital channels on render.
    digitalChannelsRendered = 0;

    colorMap: { [key: number]: string } = {};
    channelLookup: { [fcuChannelId: number]: Channel } = {};
    isViewLoaded = false;

    graphHeight: number;

    dataUpdateSubscription: Subscription;

    constructor(
        private channelService: ChannelService,
        public responsive: ResponsiveService,
        private cdr: ChangeDetectorRef,
    ) {
        this.digitalPlotter = this.digitalPlotter.bind(this);
        this.onDataHighlighed = this.onDataHighlighed.bind(this);
        this.onDataUnhighlighted = this.onDataUnhighlighted.bind(this);

        this.digitalSeriesOptions = {
            plotter: this.digitalPlotter,
            strokeWidth: 10,
        };
    }

    ngOnInit(): void {
        this.graphValues = Array(this.maximumValues);
        this.labels = Array(this.maximumLines + 1).fill('');
        this.labels[0] = 'Time';

        this.colors = Array(this.maximumLines).fill('');

        for (let i = 0; i < this.maximumValues; i++) {
            const arr = Array(this.maximumLines + 1).fill(null);
            arr[0] = i;
            this.graphValues[i] = arr;
        }

        if (this.responsive.isSmall()) {
            this.axisLabelFontSize = 8;
        } else if (this.responsive.isXSmall()) {
            this.axisLabelFontSize = 5;
        }
        this.cdr.markForCheck();
    }

    ngAfterViewInit(): void {
        this.valueRange[0] = this.displayMinY;
        this.valueRange[1] = this.displayMaxY;
        const updateZoom = this.handleZoomChanged.bind(this);
        const unhighlightPointer = this.onDataUnhighlighted;

        const intercationModel = new DygraphInteractionModel(
            Dygraph,
            updateZoom,
            unhighlightPointer,
        );

        this.graph = new Dygraph(this.graphContainer.nativeElement, this.graphValues, {
            width: this.width,
            height: this.height,

            gridLinePattern: [3, 3],
            gridLineWidth: 1,
            gridLineColor: '#8e9397',

            highlightCircleSize: 0,
            strokeWidth: 1,
            yRangePad: 1,

            showRoller: false,
            showLabelsOnHighlight: false,
            connectSeparatedPoints: true,

            valueRange: this.valueRange,

            axes: {
                x: {
                    valueFormatter: Dygraph.dateString_,
                    axisLabelFormatter: (d) => this.formatTime(new Date(d)),
                    axisLabelFontSize: this.axisLabelFontSize,
                },
                y: {
                    axisLabelFormatter: (v) => (v > this.displayMaxY ? '' : Math.floor(v)),
                    axisLabelFontSize: this.axisLabelFontSize,
                },
            },

            interactionModel: {
                mousedown: intercationModel.mouseDown.bind(intercationModel),
                mousemove: intercationModel.mouseMove.bind(intercationModel),
                mouseup: intercationModel.mouseUp.bind(intercationModel),
                touchstart: intercationModel.touchStart.bind(intercationModel),
                touchmove: intercationModel.touchMove.bind(intercationModel),
                touchend: intercationModel.touchEnd.bind(intercationModel),
            },
            highlightCallback: this.onDataHighlighed,
            unhighlightCallback: this.onDataUnhighlighted,
            underlayCallback: () => {
                this.digitalChannelsRendered = 0;
            },
        });

        this.cdr.markForCheck();
        this.cdr.detectChanges();

        if (!this.dataUpdateSubscription) {
            this.dataUpdateSubscription = this.onDataUpdate.subscribe((next) => {
                this.reshapeData(next);
            });
        }
    }

    ngAfterViewChecked(): void {
        if (this.tooltipStyle.display === 'none') {
            const divisionWidth = this.graphContainer.nativeElement.offsetWidth;
            const divisionHeight = this.graphContainer.nativeElement.offsetHeight;
            if (this.graphMinHeight && this.graph.width_ !== divisionWidth) {
                this.graph.resize(divisionWidth, divisionHeight);
                this.cdr.markForCheck();
            }
        }
    }

    onDataHighlighed(e, x, points, row, seriesName) {
        //graph.toDomXCoord is buggy. This might be due to that we're modifying the graphData array live.
        //so we're manually calculating the value that it would give us if it was correct.
        const area = this.graph.plotter_.area;
        const pointLeftCoord =
            area.x +
            ((row - this.displayMinXIndex) / (this.displayMaxXIndex - this.displayMinXIndex)) *
                area.w;

        this.visualUpdatesPaused = true;

        this.pointerStyle.display = 'block';
        this.pointerStyle.left = pointLeftCoord - this.POINTER_WIDTH / 2 + 'px';

        this.verticalRangefinderStyle.display = 'block';
        this.verticalRangefinderStyle.left = pointLeftCoord - 1 + 'px';

        this.horizontalRangefinderStyle.display = 'block';

        // using the native element style makes it appear immediately so we can get its bounds
        this.tooltip.nativeElement.style.display = 'block';
        const tooltipWidth = this.tooltip.nativeElement.offsetWidth;
        const graphWidth = this.graphContainer.nativeElement.offsetWidth;
        const isTooltipOverflowing = pointLeftCoord + tooltipWidth > graphWidth;

        if (isTooltipOverflowing) {
            this.tooltipStyle.right = graphWidth - pointLeftCoord + 'px';
            this.tooltipStyle.left = null;
        } else {
            this.tooltipStyle.right = null;
            this.tooltipStyle.left = pointLeftCoord + 'px';
        }

        this.tooltipDate = new Date(x);
        this.tooltipData = points.map((v, i) => {
            return {
                color: this.labelToColorMap[v.name],
                label: v.name,
                value: this.graphValueToOriginal(v.name, v.yval),
                unit: this.labelToChannelMap[v.name].opsData.measurementUnit,
            };
        });
    }

    onDataUnhighlighted() {
        this.visualUpdatesPaused = false;
        this.verticalRangefinderStyle.display = 'none';
        this.horizontalRangefinderStyle.display = 'none';
        this.pointerStyle.display = 'none';
        this.tooltip.nativeElement.style.display = 'none';
    }

    updateVerticalPointerAlignment(event?) {
        if (!event) {
            this.tooltipStyle = {
                display: 'none',
                left: '0px',
                right: null,
                top: '0px',
            };
            return;
        }
        this.pointerStyle.top = event.offsetY - this.POINTER_WIDTH / 2 + 'px';
        this.horizontalRangefinderStyle.top = event.offsetY - 1 + 'px';
        this.tooltipStyle.top =
            event.offsetY - Math.round(this.tooltip.nativeElement.offsetHeight / 2) + 'px';
    }

    formatTime(date: Date) {
        return (
            this.padWithZero(date.getHours()) +
            ':' +
            this.padWithZero(date.getMinutes()) +
            ':' +
            this.padWithZero(date.getSeconds())
        );
    }

    padWithZero(x) {
        return x < 10 ? '0' + x : x;
    }

    ngOnDestroy(): void {
        this.dataUpdateSubscription?.unsubscribe();
        this.graph?.destroy();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let isChanged = false;
        let isDimensionsChanged = false;

        if (changes.displayMinY !== undefined) {
            this.valueRange[0] = this.displayMinY;
            isChanged = true;
        }

        if (changes.displayMaxY !== undefined) {
            this.valueRange[1] = this.displayMaxY;
            isChanged = true;
        }

        if (changes.displayMinXIndex !== undefined) {
            this.dateRange[0] = this.displayMinXIndex;
            isChanged = true;
        }

        if (changes.displayMaxXIndex !== undefined) {
            this.dateRange[1] = this.displayMaxXIndex;
            isChanged = true;
        }

        if (changes.view && this.view != null) {
            this.colorMap = {};

            for (let i = 0; i < this.view.channels.length; i++) {
                const viewOptions = this.view.channels[i];
                this.colorMap[viewOptions.fcuChannelId] = viewOptions.color;
            }

            this.reloadChannels();
        }

        if (changes.width || changes.height) {
            isDimensionsChanged = true;
        }

        if (this.graph) {
            if (isDimensionsChanged) {
                this.graph.resize(this.width, this.height);
            }

            if (isChanged) {
                this.recalculateValueRange();
                const updateProps = {
                    valueRange: this.valueRange,
                    dateWindow: [
                        this.graphValues[this.displayMinXIndex][0],
                        this.graphValues[this.displayMaxXIndex][0],
                    ],
                };

                this.graph.updateOptions(updateProps);
            }
        }
    }

    private async reloadChannels() {
        this.isViewLoaded = false;
        const newChannelLookup = {};

        //Load channels that aren't already loaded
        let channels = await Promise.all(
            this.view.channels.map((viewOptions) =>
                viewOptions.fcuChannelId in this.channelLookup
                    ? this.channelLookup[viewOptions.fcuChannelId]
                    : lastValueFrom(
                          this.channelService.getByFcuChannelId(viewOptions.fcuChannelId),
                      ),
            ),
        );

        //Map channels to channelLookup
        for (let i = 0; i < channels.length; i++) {
            newChannelLookup[channels[i].fcuData.fcuChannelId] = channels[i];
        }

        this.channelLookup = newChannelLookup;
        this.isViewLoaded = true;
        this.onViewLoaded.emit();
    }

    reshapeData(data: ChannelHistory): void {
        if (this.visualUpdatesPaused || !this.isViewLoaded) {
            return;
        }

        const graphOptions = {
            file: this.graphValues,
            labels: this.labels,
            colors: this.colors,
            dateWindow: this.dateRange,
            valueRange: this.valueRange,
            series: {},
        };

        const colorMap = {};
        const labelMap = {};
        const scaleMap = {};
        let digitalChannelCount = 0;

        //The server should return times aligned to the update frequency.
        const latestTick = data.currentTime;

        //We want digital channels to render last, so we put them at the end of the list.
        let analogWriteIndex = 1;
        let digitalWriteIndex = this.maximumLines + 1;

        //Write the timestamps of the entries
        for (let j = 0; j < this.graphValues.length; j++) {
            this.graphValues[j][0] =
                latestTick - (this.graphValues.length - 1 - j) * data.updateInterval;
        }

        //Copy values to graphValues
        for (let i = 0; i < data.histories.length; i++) {
            const history = data.histories[i];
            const channel = this.channelLookup[history.fcuChannelId];
            const label = this.getLabel(history.fcuChannelId);
            const isDigitalChannel = this.isDigital(history.fcuChannelId);

            let columnIndex = 0;

            let valueRange = channel
                ? channel.fcuData.measurableRange.max * channel.fcuData.scale -
                  channel.fcuData.measurableRange.min * channel.fcuData.scale
                : 1;

            const nullsToAppend = Math.floor(
                (latestTick - history.lastUpdated) / data.updateInterval,
            );
            let index = Math.max(this.maximumValues - history.log.length - nullsToAppend, 0);
            const historyStartIndex =
                history.log.length + nullsToAppend > this.maximumValues ? nullsToAppend : 0;

            if (isDigitalChannel) {
                graphOptions.series[label] = this.digitalSeriesOptions;
                columnIndex = --digitalWriteIndex;
                digitalChannelCount++;
                valueRange = 1;
            } else {
                columnIndex = analogWriteIndex++;
            }

            const scale = 100 / valueRange;

            this.labels[columnIndex] = label;
            this.colors[columnIndex - 1] = this.colorMap[history.fcuChannelId];

            colorMap[label] = this.colorMap[history.fcuChannelId];
            labelMap[label] = this.channelLookup[history.fcuChannelId];
            scaleMap[label] = scale;

            //Null out left side entries
            for (let i = 0; i < index; i++) {
                this.graphValues[i][columnIndex] = null;
            }

            //Fill in relevant history data
            for (let j = historyStartIndex; j < history.log.length; j++, index++) {
                const value = history.log.getValue(j);
                this.graphValues[index][columnIndex] =
                    value == null ? null : history.log.getValue(j) * scale;
            }

            //Null out right side entries
            for (; index < this.maximumValues; index++) {
                this.graphValues[index][columnIndex] = null;
            }
        }

        //Clear unused colors and labels. Null out unused columns.
        for (let j = analogWriteIndex; j < digitalWriteIndex; j++) {
            this.labels[j] = '';
            this.colors[j - 1] = '';

            for (let k = 0; k < this.maximumValues; k++) {
                this.graphValues[k][j] = null;
            }
        }

        this.dateRange[0] = this.graphValues[this.displayMinXIndex][0];
        this.dateRange[1] = this.graphValues[this.displayMaxXIndex][0];

        this.labelToColorMap = colorMap;
        this.labelToChannelMap = labelMap;
        this.labelToScaleMap = scaleMap;
        this.digitalChannelCount = digitalChannelCount;
        this.recalculateValueRange();

        this.graph.updateOptions(graphOptions);
    }

    isDigital(fcuChannelId: number) {
        return (
            this.channelLookup[fcuChannelId] &&
            ChannelFns.isDigital(this.channelLookup[fcuChannelId])
        );
    }

    digitalPlotter(e) {
        let ctx = e.drawingContext;

        if (this.digitalChannelsRendered == 0) {
            const bg = window.getComputedStyle(this.graphContainer.nativeElement).backgroundColor;
            const digitalChannelCount = e.seriesCount - e.seriesIndex;
            const width = this.graphContainer.nativeElement.offsetWidth;
            const height =
                this.DIGITAL_CHANNEL_START + digitalChannelCount * this.DIGITAL_CHANNEL_DISTANCE;
            ctx.fillStyle = bg;
            ctx.fillRect(0, 0, width, height);
        }

        const y =
            this.DIGITAL_CHANNEL_START +
            this.digitalChannelsRendered++ * this.DIGITAL_CHANNEL_DISTANCE;
        const points = e.points;

        const barOffset = points.length >= 2 ? (points[1].canvasx - points[0].canvasx) / 2 : 0;

        ctx.beginPath();
        ctx.strokeStyle = this.labelToColorMap[e.name];
        ctx.lineWidth = e.strokeWidth;
        for (let i = 1; i < points.length; i++) {
            if (points[i].yval) {
                ctx.lineTo(points[i].canvasx + barOffset, y);
            } else {
                ctx.moveTo(points[i].canvasx + barOffset, y);
            }
        }
        ctx.stroke();

        ctx.beginPath();
        ctx = e.drawingContext;
        ctx.strokeStyle = this.labelToColorMap[e.name];
        ctx.lineWidth = e.strokeWidth / 4;
        for (let i = 1; i < points.length; i++) {
            if (points[i].yval === 0) {
                ctx.lineTo(points[i].canvasx + barOffset, y);
            } else {
                ctx.moveTo(points[i].canvasx + barOffset, y);
            }
        }
        ctx.stroke();
    }

    recalculateValueRange() {
        const digitalPaddingPx =
            this.DIGITAL_CHANNEL_START + this.digitalChannelCount * this.DIGITAL_CHANNEL_DISTANCE;
        const paddingRatio = digitalPaddingPx / this.graphContainer.nativeElement.offsetHeight;
        const displayedRange = this.valueRange[1] - this.valueRange[0];
        const paddingValue = Math.ceil(displayedRange * paddingRatio);
        this.valueRange[1] = this.displayMaxY + paddingValue;
    }

    getLabel(channelNo: number): string {
        return this.channelLookup && this.channelLookup[channelNo]
            ? this.channelLookup[channelNo].opsData.name
            : '' + channelNo;
    }

    graphValueToOriginal(seriesName, value) {
        const channel = this.labelToChannelMap[seriesName];
        const graphScale = this.labelToScaleMap[seriesName] || 1;
        const scaledNumericValue = Math.round(value / graphScale);

        return value == null ? null : ChannelFns.transformValue(channel, scaledNumericValue);
    }

    setChannelVisibility(visibilityMap: { [channelNumber: number]: boolean }) {
        const labelToIndexMap = {};

        for (let i = 1; i < this.labels.length; i++) {
            labelToIndexMap[this.labels[i]] = i - 1;
        }

        const dyGraphVisibilityMap = {};

        for (const channel in visibilityMap) {
            const label = this.getLabel(Number(channel));

            if (label in labelToIndexMap) {
                dyGraphVisibilityMap[labelToIndexMap[label]] = visibilityMap[channel];
            }
        }

        this.graph.setVisibility(dyGraphVisibilityMap);
    }

    resetVisibileChannels() {
        const indices = Array(this.maximumLines);
        for (let i = 0; i < this.maximumLines; i++) {
            indices[i] = i;
        }
        this.graph.setVisibility(indices, true);
    }

    addRipple(event: MouseEvent) {
        const rippleComponent = document.createElement('div');
        rippleComponent.classList.add('graph-click-ripple');
        rippleComponent.style.top = this.pointerStyle.top;
        rippleComponent.style.left = this.pointerStyle.left;

        this.parentElement.nativeElement.appendChild(rippleComponent);

        rippleComponent.addEventListener('animationend', () => {
            rippleComponent.remove();
        });
    }

    getCsvData(): (number | string)[][] {
        const returnData = Array(this.maximumValues + 1);
        const returnLabels = [this.labels[0]];
        const returnSeries = [0];

        returnData[0] = returnLabels;

        for (let i = 1; i < this.labels.length; i++) {
            if (this.labels[i] != '') {
                returnLabels.push(this.labels[i]);
                returnSeries.push(i);
            }
        }

        for (let i = 0; i < this.graphValues.length; i++) {
            const row = Array(returnSeries.length);
            row[0] = dayjs(this.graphValues[i][0]).format('HH:mm:ss');

            returnData[i + 1] = row;

            for (let j = 1; j < returnSeries.length; j++) {
                const label = this.labels[returnSeries[j]];
                const graphValue = this.graphValues[i][returnSeries[j]];
                row[j] = this.graphValueToOriginal(label, graphValue);
            }
        }

        return returnData;
    }

    getAsImage() {
        return domtoimage.toBlob(this.graphContainer.nativeElement);
    }

    handleZoomChanged = (minX: number, maxX: number) => {
        this.updateMaxXIndex(maxX);
        this.updateMinXIndex(minX);
        this.displayMaxXIndexChange.emit(this.displayMaxXIndex);
        this.displayMinXIndexChange.emit(this.displayMinXIndex);
    };

    private updateMinXIndex(minX: number) {
        if (this.displayMinXIndex === this.MIN_DATE_INDEX) {
            this.displayMinXIndex = minX;
        } else {
            this.displayMinXIndex = this.displayMinXIndex + minX;
        }
    }

    private updateMaxXIndex(maxX: number) {
        if (this.displayMaxXIndex === this.MAX_DATE_INDEX) {
            this.displayMaxXIndex = maxX;
        } else {
            this.displayMaxXIndex = this.displayMinXIndex + maxX;
        }
    }
}
